import { card as parse, type CardId } from '@rummy/engine';
import { memo } from 'react';
import { useI18n } from '../i18n/index.tsx';

const SUIT_GLYPH: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const SUIT_NAME: Record<string, string> = {
  S: 'spades',
  H: 'hearts',
  D: 'diamonds',
  C: 'clubs',
};

export interface PlayingCardProps {
  id: CardId;
  /** Wild this hand — either a printed joker or the cut rank (§3.1). */
  wild?: boolean;
  faceDown?: boolean;
  selected?: boolean;
  dragging?: boolean;
  dimmed?: boolean;
  /** Freshly drawn and not yet placed — the "held" state of §6.3. */
  held?: boolean;
  /**
   * Draw the body of the card, not only its corner.
   *
   * Set where the whole card is visible — the open pile, the cut card, the
   * declaration modal, the summary. Never in the tray: a fanned hand shows a
   * 30px sliver and anything in the body of the card is ink nobody can see.
   */
  feature?: boolean;
  className?: string;
}

/**
 * The court figures.
 *
 * A J/Q/K that is only a letter is the clearest "unfinished" signal a card game
 * can send, and the corner-index argument does not apply to a card the player
 * is looking at whole. These are heraldic marks rather than illustrated
 * figures: a crown, a coronet, a halberd — drawn once, in the suit's own
 * colour, and never competing with the index for attention.
 */
const COURT: Record<string, string> = {
  // Crown.
  K: 'M12 46h40l4-24-12 9-12-15-12 15-12-9z M12 46h40v6H12z',
  // Coronet, lower and rounder than the king's.
  Q: 'M14 46h36l3-19-9 6-6-12-6 12-6-12-6 12-6-6z M14 46h36v6H14z',
  // Halberd on a banner.
  J: 'M30 8h4v48h-4z M22 14h20l-5 7 5 7H22z M26 56h12v4H26z',
};

/**
 * A card face (§6.5).
 *
 * One index and one suit mark, set large and at full strength, on the diagonal.
 * A full pip grid is more faithful to a printed deck but at 78 × 110 with
 * thirteen cards fanned it is noise: the eye is reading a 30px sliver, and the
 * only things that survive at that size are the index and its colour. Reducing
 * the face to exactly those two marks is what "reduced pip density" was asking
 * for all along.
 *
 * The index glyph is locale-dependent — J/Q/K are V/D/R in a French deck (§10)
 * — so it is a themed asset rather than a hardcoded character.
 */
export const PlayingCard = memo(function PlayingCard({
  id,
  wild = false,
  faceDown = false,
  selected = false,
  dragging = false,
  dimmed = false,
  held = false,
  feature = false,
  className = '',
}: PlayingCardProps) {
  const { t, cardIndex } = useI18n();

  if (faceDown) {
    return (
      <div className={`card card--back ${className}`} aria-hidden="true">
        <span className="card__back-frame" />
        <span className="card__back-mark" />
      </div>
    );
  }

  const c = parse(id);
  const suit = c.suit ?? 'S';
  const glyph = SUIT_GLYPH[suit] ?? '♠';
  const rank = (c.rank ?? '') as string;
  const label = c.printedJoker
    ? t('a11y.joker')
    : t('a11y.card', { rank, suit: SUIT_NAME[suit] ?? suit });

  return (
    <div
      className={[
        'card',
        `card--${suit}`,
        c.printedJoker ? 'card--joker' : '',
        rank === '10' ? 'card--wide-index' : '',
        feature ? 'card--feature' : '',
        selected ? 'is-selected' : '',
        dragging ? 'is-dragging' : '',
        dimmed ? 'is-dimmed' : '',
        held ? 'is-held' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="img"
      aria-label={label}
    >
      {c.printedJoker ? (
        /* A joker has no rank and no suit, so it gets one mark, not two. */
        <span className="card__index" aria-hidden="true">
          ★
        </span>
      ) : (
        <>
          <span className="card__index" aria-hidden="true">
            {cardIndex(rank)}
          </span>
          <span className="card__mark" aria-hidden="true">
            {glyph}
          </span>
          {feature &&
            (COURT[rank] ? (
              <svg className="card__court" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
                <path d={COURT[rank]} />
              </svg>
            ) : (
              /* Number cards get the suit again, large and faint — enough to
                 give the body of the card something to be. */
              <span className="card__body-mark" aria-hidden="true">
                {glyph}
              </span>
            ))}
        </>
      )}
      {wild && <span className="card__wild" aria-hidden="true" />}
    </div>
  );
});
